/**
 * OIDC bootstrap.
 *
 * The backend's `/api/v1/auth/config` endpoint is the single source of truth
 * for OIDC settings. We fetch it once at startup and feed it into
 * `react-oidc-context`'s `AuthProvider`. This way we never bake issuer URLs
 * into the SPA bundle — the same Vite build is reused across dev, staging,
 * and on-premise deployments.
 */

import { useEffect, useState } from "react";
import { AuthProvider as OidcAuthProvider } from "react-oidc-context";
import { WebStorageStateStore } from "oidc-client-ts";

interface AuthConfigPayload {
  issuer: string;
  client_id: string;
  audience: string;
  scopes: string[];
}

interface AuthBootProps {
  children: React.ReactNode;
}

async function fetchAuthConfig(): Promise<AuthConfigPayload> {
  const response = await fetch("/api/v1/auth/config");
  if (!response.ok) {
    throw new Error(`auth config failed with ${response.status}`);
  }
  return (await response.json()) as AuthConfigPayload;
}

function onSigninCallback() {
  // Strip the OIDC params (`code`, `state`, `session_state`) from the URL.
  window.history.replaceState({}, document.title, window.location.pathname);
}

export function AuthBoot({ children }: AuthBootProps) {
  const [config, setConfig] = useState<AuthConfigPayload | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchAuthConfig()
      .then(setConfig)
      .catch((cause: Error) => setError(cause.message));
  }, []);

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center p-6 text-sm text-rose-600">
        {error}
      </div>
    );
  }
  if (!config) {
    return (
      <div className="flex min-h-screen items-center justify-center p-6 text-sm text-slate-500">
        Cargando configuración…
      </div>
    );
  }

  return (
    <OidcAuthProvider
      authority={config.issuer}
      client_id={config.client_id}
      redirect_uri={`${window.location.origin}/`}
      post_logout_redirect_uri={`${window.location.origin}/`}
      scope={config.scopes.join(" ")}
      response_type="code"
      automaticSilentRenew
      onSigninCallback={onSigninCallback}
      userStore={new WebStorageStateStore({ store: window.localStorage })}
    >
      {children}
    </OidcAuthProvider>
  );
}
